import { ManaType } from '@/components/Mana/Mana'

type ArticleAuthorType = {
  avatar: string
  id: number
  link: string
  name: string
}

type ArticleSiteType = {
  api: string
  id: string
  name: string
  parent_page_id: number
  tags: boolean
}

type PagePanelType = {
  alt: string
  href: string
  value: string
}

type ArchidektPlaytesterCardType = {
  c: string
  f: number
  q: number
  u: string
}

export type ArticleListEntryType = PagePanelType & {
  author: ArticleAuthorType
  date: string
  excerpt: string
  media: string
  site: ArticleSiteType
  link?: string
  href?: string
  tags: undefined
  title?: string
  value?: string
}

type LinkType = {
  header: string
  items: {
    href: string
    value: string
    current: boolean
  }[]
  separator?: boolean
}

type ComboCountType = PagePanelType

export type PieChartType = {
  title: string
  content: {
    label: string
    value: number
    color: string
  }[]
}

type TagType = {
  id: number
  link: string
  name: string
}

type RelatedVideoType = {
  author: ArticleAuthorType
  cards: string[]
  categories: {
    id: number
    link: string
    name: string
  }[]
  date: string
  date_gmt: string
  excerpt: string
  id: number
  link: string
  media: string
  modified_gmt: string
  site: ArticleSiteType
  tags: TagType[]
  title: string
  youtube?: string
}

export type TribeLinkType = {
  count: number
  'href-suffix': string
  value: string
}
export type PartnerCountType = {
  count: number
  href?: string
  value: string
}

type PriceWithUrlType = {
  price: number
  url: string
}

type PriceWithSlugType = {
  price: number
  slug: string
}

export type PriceMapType = {
  cardhoarder: PriceWithUrlType
  cardkingdom: PriceWithUrlType
  cardmarket: PriceWithUrlType
  face2face: PriceWithUrlType
  mtgstocks: PriceWithUrlType
  scg: PriceWithSlugType
  tcgplayer: PriceWithUrlType
  tcgl: PriceWithSlugType
  coolstuffinc?: PriceWithUrlType // Deprecated
}

type UniqueArtType = {
  artist: string
  collector_number: string
  set: string
  set_name: string
  image_uris: string[]
}

export type ScryfallImageType = {
  art_crop: string
  border_crop: string
  large: string
  normal: string
  png: string
  small: string
}

type ExtendedCardType = {
  object: string
  id: string
  oracle_id: string
  multiverse_ids: number[]
  mtgo_id: number
  tcgplayer_id: number
  cardmarket_id: number
  name: string
  lang: string
  released_at: string
  uri: string
  scryfall_uri: string
  layout: string
  highres_image: boolean
  image_status: string
  image_uris: ScryfallImageType
  mana_cost: string
  cmc: number
  type_line: string
  oracle_text: string
  power: string
  toughness: string
  colors: string[]
  color_identity: string[]
  keywords: string[]
  legalities: Record<string, 'legal' | 'not_legal' | 'banned' | 'restricted'>
  games: string[]
  reserved: boolean
  foil: boolean
  nonfoil: boolean
  finishes: string[]
  oversized: boolean
  promo: boolean
  reprint: boolean
  variation: boolean
  set_id: string
  set_name: string
  set_type: string
  set_uri: string
  set_search_uri: string
  scryfall_set_uri: string
  rulings_uri: string
  prints_search_uri: string
  collector_number: string
  digital: boolean
  rarity: string
  card_back_id: string
  artist: string
  artist_ids: string[]
  illustration_id: string
  border_color: string
  frame: string
  frame_effects: string[]
  security_stamp: string
  full_art: boolean
  textless: boolean
  booster: boolean
  story_spotlight: boolean
  edhrec_rank: number
  penny_rank: number
  preview?: {
    source: string
    source_uri: string
    previewed_at: string
  }
  prices: PriceMapType
  related_uris: {
    gatherer: string
    tcgplayer_infinite_articles: string
    tcgplayer_infinite_decks: string
    edhrec: string
  }
  scryfall_name: string
  names: string[]
  banned: boolean
  legal_commander: boolean
  legal_partner: boolean
  legal_companion: boolean
  precon: boolean
  salt: number
  salt_2021: number
  salt_2020: number
  salt_2019: number
  earliest_set: string
  latest_print: string
  latest_set: string
  sets: string[]
  news: false
  unique_artwork: UniqueArtType[]
  unofficial: false
  aetherhub_uri: string
  deckstats_uri: string
  moxfield_uri: string
  archidekt_uri: string
  mtggoldfish_uri: string
  url: string
  spellbook_uri: string | null
  type: string
  subtypes: string[]
  supertypes: string[]
  types: string[]
  primary_type: string
  sanitized_wo?: undefined
  background_chooser?: boolean
  doctors_companion?: boolean
}

export type CardType = {
  aetherhub_uri: string
  archidekt_uri: string
  background_chooser?: string
  banned?: boolean
  card_market_id?: number
  cards?: ExtendedCardType[] | CardType[]
  cmc: number
  color_identity: string[]
  deckstate_uri?: string
  doctors_companion?: boolean
  image_uri?: string
  image_uris: string[]
  is_partner?: boolean
  layout: string
  legal_commander: boolean
  legal_companion?: boolean
  legal_partner?: boolean
  mana_cost?: string
  moxfield_uri: string
  mtggoldfish_uri: string
  name: string
  names: string[]
  new?: boolean
  oracle_text?: string
  partners_with?: string
  precon?: boolean
  prices: PriceMapType
  primary_type: string
  rarity: string
  salt: number
  sanitized?: string
  sanitized_wo?: string
  scryfall_uri: string
  sets?: string[]
  spellbook_uri?: string
  tags?: string[]
  type: string
  types?: string[]
  unique_artwork?: UniqueArtType[]
  unofficial?: boolean
  url: string | null
  power?: string
  toughness?: string
}

type CardFaceType = {
  banned: boolean
  background_chooser?: boolean
  color_identity: string[]
  cmc: number
  doctors_companion?: boolean
  layout: string
  legal_commander: boolean
  legal_companion?: boolean
  legal_partner?: boolean
  name: string
  prices: PriceMapType
  primary_type: string
  rarity: string
  salt: number
  unnoficial: boolean
  url: string
}

export type PageCardType = CardType & {
  inclusion: number
  num_decks: number
  synergy: number
  potential_decks: number
  cards: CardFaceType[]
  is_commander: boolean
  label: string
  legal_commander: boolean
  image_uris: ScryfallImageType[]
  color_identity: string
}

export type SimpleRecCardType = {
  name: string
  sanitized: string
  sanitized_wo: string
  url: string
  synergy: number
  num_decks: number
  potential_decks: number
}

export type RecCardType = {
  name: string
  sanitized: string
  sanitized_wo: string
  url: string
  cards: { name: string; url: string }[]
  synergy: number
  inclusion: number
  label: string
  num_decks: number
  potential_decks: number
  rank?: number
  unpopularity?: number
}

export type CardListType = {
  cardviews: RecCardType[]
  header: string
  tag: string
  href?: string
  more?: string
  url?: string
}

type DeckInfoType = {
  deck_preview: {
    artifact: number
    cards: string[]
    cedh: boolean
    coloridentity: string[]
    commanders: Array<string | null>
    creature: number
    deckage: number
    edhrectags: string[]
    enchantment: number
    header: string
    instant: number
    land: number
    pcommander: Array<string | null>
    planeswalker: number
    price: number
    salt: number
    savedate: string
    second_row: string
    show_savedate: boolean
    show_url: boolean
    similardecks: string[]
    sorcery: number
    tags: string[]
    theme?: string
    tribe?: string
    url: string
    urlhash: string
  }
}

export type GenericPageType = {
  cardlist: SimpleRecCardType[]
  container: {
    description: string
    json_dict: {
      cardlists?: CardListType[]
      card: PageCardType
    }
    keywords: string
    title: string
  }
  description: string
  header: string
  iframes?: string
  panels?: {
    articles?: ArticleListEntryType[]
  }
}

export type CommanderPageType = GenericPageType & {
  archidekt: ArchidektPlaytesterCardType[]
  creature: number
  instant: number
  sorcery: number
  artifact: number
  enchantment: number
  planeswalker: number
  land: number
  basic: number
  nonbasic: number

  similar: CardType[]

  panels: {
    deckinfo?: DeckInfoType
    articles: ArticleListEntryType[]
    combocounts: ComboCountType[]
    links: LinkType[]
    mana_curve: Record<string, number>
    piechart: PieChartType
    related_video: RelatedVideoType
    tribelinks: {
      budget: TribeLinkType[]
      themes: TribeLinkType[]
    }
  }

  coalesce?: {
    image: string
    product_name: string
    product_url: string
  }
  partnercounts: PartnerCountType[]
  deck?: string[]
}

export type DeckTableEntryType = {
  artifact: number
  battle: number
  creature: number
  enchantment: number
  instant: number
  planeswalker: number
  price: number
  salt: number
  savedate: string
  sorcery: number
  theme: string | null
  tribe: string | null
  urlhash: string
}

export type DecksTablePageType = CommanderPageType & {
  table: DeckTableEntryType[]
}

export type CombosPageType = GenericPageType & {
  related_info: Array<{
    header: string
    items: Array<{
      colors: ManaType[]
      count: number
      textLeft: string
      textRight?: string
      url: string
    }>
  }>
}

export type ThemesPageType = GenericPageType & {
  relatedinfo?: {
    name: string
    singular: string
    colors: Array<{
      count: number
      icons: string
      name: string
      url: string
    }>
  }
}

export type IndividualComboPageType = GenericPageType & {
  combo: {
    combo_id: string
    process_txt: string[]
    require_txt: string
    results_txt: string
  }
}

export type SetsPageType = {
  data: Array<{
    code: string
    name: string
    icon_svg_uri: string
  }>
}

export type PagedDataType = {
  cardviews: RecCardType[]
  more?: string
}

export const TIMESPANS = [undefined, 'week', 'month', 'year']

export const COLORS = [
  'w',
  'u',
  'b',
  'r',
  'g',
  'wu',
  'ub',
  'br',
  'rg',
  'gw',
  'wb',
  'ur',
  'bg',
  'rw',
  'gu',
  'wub',
  'ubr',
  'brg',
  'rgw',
  'gwu',
  'wbg',
  'urw',
  'bgu',
  'rwb',
  'gur',
  'wubr',
  'ubrg',
  'brgw',
  'rgwu',
  'gwub',
  'wubrg',
  'colorless',
]
